// ─────────────────────────────────────────────────────────────────────────────
// WorkoutStrokesReplay — drill-in stroke-by-stroke viewer for WorkoutHistory.
//
// Reads `workout.stroke_arrays` (saved from WorkoutContext during the live
// session) and lets the user scrub through every stroke in the row using a
// slider. The actual rendering is delegated to the existing StrokeVisualizer
// via a `crewz-gate-stroke` window-event dispatch — keeping a single source of
// truth for the chart shape.
//
// Renders nothing if the workout has no captured strokes (older sessions or
// hardware-not-yet-streaming runs).
// ─────────────────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import { ChevronLeft, ChevronRight, Pause, Play, Rewind } from 'lucide-react';
import StrokeVisualizer from './StrokeVisualizer';

const PLAYBACK_INTERVAL_MS = 700;

export default function WorkoutStrokesReplay({ workout }) {
  const strokes = useMemo(() => Array.isArray(workout?.stroke_arrays) ? workout.stroke_arrays : [], [workout]);
  const [index, setIndex] = useState(0);
  const [playing, setPlaying] = useState(false);

  // Push the current stroke to the visualizer. It already renders all metrics.
  useEffect(() => {
    if (strokes.length === 0) return;
    const s = strokes[index];
    if (!s) return;
    try {
      window.dispatchEvent(new CustomEvent('crewz-gate-stroke', {
        detail: {
          side: s.side || 'sweep',
          forceN: s.forceN,
          angleDeg: s.angleDeg,
          rigging: s.rigging || null,
          spm: s.spm,
          strokeIndex: s.i ?? index,
          timestamp: s.t || Date.now(),
        },
      }));
    } catch (_e) { /* noop */ }
  }, [index, strokes]);

  // Auto-advance playback
  useEffect(() => {
    if (!playing || strokes.length === 0) return undefined;
    const id = setInterval(() => {
      setIndex(prev => {
        const next = prev + 1;
        if (next >= strokes.length) {
          setPlaying(false);
          return prev;
        }
        return next;
      });
    }, PLAYBACK_INTERVAL_MS);
    return () => clearInterval(id);
  }, [playing, strokes.length]);

  if (strokes.length === 0) return null;

  const total = strokes.length;
  const current = strokes[index] || {};
  const sec = current.t ? Math.round((current.t - (strokes[0].t || current.t)) / 1000) : 0;
  const mm = String(Math.floor(sec / 60)).padStart(2, '0');
  const ss = String(sec % 60).padStart(2, '0');

  return (
    <div className="mb-6 bg-white dark:bg-zinc-800 border-2 border-zinc-200 dark:border-zinc-700 rounded-xl p-4 shadow-sm" data-testid="workout-strokes-replay">
      <div className="flex items-center justify-between mb-3">
        <div>
          <h4 className="text-sm font-bold uppercase tracking-wider text-zinc-700 dark:text-zinc-200">
            Stroke Replay
          </h4>
          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">
            {total} strokes captured · scrub to inspect any single stroke from this row
          </p>
        </div>
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wider text-zinc-500 dark:text-zinc-400">Stroke</div>
          <div className="text-base font-mono font-bold text-zinc-900 dark:text-white" data-testid="workout-strokes-replay-counter">
            {index + 1} / {total}
          </div>
          <div className="text-[10px] font-mono text-zinc-500 dark:text-zinc-400">@ {mm}:{ss}</div>
        </div>
      </div>

      {/* Slider */}
      <input
        type="range"
        min="0"
        max={total - 1}
        value={index}
        onChange={(e) => { setPlaying(false); setIndex(Number(e.target.value)); }}
        className="w-full h-2 bg-zinc-200 dark:bg-zinc-700 rounded-lg appearance-none cursor-pointer accent-rose-600"
        data-testid="workout-strokes-replay-slider"
      />

      {/* Controls */}
      <div className="flex items-center justify-center gap-2 mt-3">
        <button
          type="button"
          onClick={() => { setPlaying(false); setIndex(0); }}
          className="p-2 rounded-lg bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition"
          data-testid="workout-strokes-replay-rewind"
          aria-label="Rewind to first stroke"
        >
          <Rewind className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => { setPlaying(false); setIndex(i => Math.max(0, i - 1)); }}
          className="p-2 rounded-lg bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition"
          data-testid="workout-strokes-replay-prev"
          aria-label="Previous stroke"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <button
          type="button"
          onClick={() => setPlaying(p => !p)}
          className="px-4 py-2 rounded-lg bg-zinc-900 dark:bg-white text-white dark:text-zinc-900 text-xs font-bold uppercase tracking-wider hover:opacity-90 transition inline-flex items-center gap-1.5"
          data-testid="workout-strokes-replay-play"
        >
          {playing ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
          {playing ? 'Pause' : 'Play'}
        </button>
        <button
          type="button"
          onClick={() => { setPlaying(false); setIndex(i => Math.min(total - 1, i + 1)); }}
          className="p-2 rounded-lg bg-zinc-100 dark:bg-zinc-700 text-zinc-700 dark:text-zinc-200 hover:bg-zinc-200 dark:hover:bg-zinc-600 transition"
          data-testid="workout-strokes-replay-next"
          aria-label="Next stroke"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
      </div>

      {/* Stroke visualizer — re-rendered with synthetic stroke events; always show during replay */}
      <div className="mt-4">
        <StrokeVisualizer hardwareConnected={true} />
      </div>
    </div>
  );
}
